import type { PositionType } from '../../minigame/match2-timestep/lib/defs/types';
import { arrayShuffle } from '../../minigame/match2-timestep/lib/util/jsTools';
import type { RandomFunction } from '../../minigame/match2-timestep/lib/util/mathTools';
import type {
  Cell,
  MapComponent,
} from '../../minigame/match2-timestep/game/components/MapComponent';
import type { BlockProps } from '../../minigame/match2-timestep/game/defs/block';
import type { BlockEntity } from '../../minigame/match2-timestep/game/entities/BlockEntity';
import type { GameScene } from '../../minigame/match2-timestep/game/GameScene';
import { waitAFrame } from '../../minigame/match2-timestep/lib/util/waitAFrame';
import { runWhirlwindDancerPowerAnimation } from './dreamDrift.power.dancer.anim';

/** Tuning values for the Whirlwind Dancer opening power. */
type WhirlwindDancerPowerConfig = {
  /** Number of board tiles crossed by the generated path. */
  pathLength: number;
  /** Minimum unique obstacles the path should contact when possible. */
  minimumObstacleContacts: number;
};

/** Current Whirlwind Dancer tuning. */
const WHIRLWIND_DANCER_POWER_CONFIG: WhirlwindDancerPowerConfig = {
  pathLength: 12,
  minimumObstacleContacts: 4,
};

/** One edge-to-edge direction considered while generating a dancer path. */
type WhirlwindDancerPathDirection = {
  /** Coordinate changed while crossing the board. */
  axis: 'x' | 'y';
  /** Coordinate value used by the starting edge. */
  start: number;
  /** Coordinate value required by the opposite edge. */
  end: number;
};

/** Search state for one partial Whirlwind Dancer path. */
type WhirlwindDancerPathState = {
  /** Current tile at the end of the partial path. */
  position: PositionType;
  /** Unique obstacle ids contacted so far, or undefined once the target is met. */
  contactedObstacleIds?: number[];
  /** Ordered board tiles in the partial path. */
  path: PositionType[];
  /** Direction of the most recent step, used to prefer bends. */
  step?: PositionType;
};

/** Orthogonal offsets available to each path step. */
const WHIRLWIND_DANCER_STEP_OFFSETS: PositionType[] = [
  { x: -1, y: 0 },
  { x: 1, y: 0 },
  { x: 0, y: -1 },
  { x: 0, y: 1 },
];

/** Contact-state marker used after the requested obstacle count is reached. */
const WHIRLWIND_DANCER_CONTACT_TARGET_MET = 'complete';

/** Eligible first-contact obstacle targets grouped by their path index. */
type WhirlwindDancerPathTargets = Map<number, BlockEntity[]>;

/** Block types that are regular tiles or player-created powers, not obstacles. */
const WHIRLWIND_DANCER_INELIGIBLE_BLOCK_TYPES: Partial<
  Record<BlockProps['type'], true>
> = {
  basic: true,
  bomb: true,
  cube: true,
  rocket: true,
};

/**
 * Generate the board path crossed by Whirlwind Dancer.
 *
 * @param map Active Match-2 board map.
 * @param config Power-local tuning for path length and useful contacts.
 * @param random Random source used to vary directions and steps.
 * @returns Ordered board positions crossed by the power.
 */
export function generateWhirlwindDancerPath(
  map: MapComponent,
  config: WhirlwindDancerPowerConfig,
  random: RandomFunction = Math.random,
): PositionType[] {
  // Ignore invalid non-positive path tuning without blocking level startup.
  const pathLength = Math.max(0, Math.floor(config.pathLength));
  if (pathLength === 0) return [];

  // Collect occupied tiles, playable bounds, and unique eligible obstacles together.
  const enabledPositions: PositionType[] = [];
  const obstacleIds = new Map<BlockEntity, number>();
  let minX = map.columns;
  let maxX = 0;
  let minY = map.rows;
  let maxY = 0;
  for (let x = 0; x < map.columns; x++) {
    for (let y = 0; y < map.rows; y++) {
      const cell = map.grid[x][y];
      // Empty and overlay-only cells cannot carry the whirlwind's path.
      if (!cell.enabled || !cell.base) continue;

      enabledPositions.push({ x, y });
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);

      for (const layer of [cell.base, cell.overlay]) {
        const entity = layer?.entity;
        if (!entity || !isWhirlwindDancerObstacle(entity)) continue;
        if (!obstacleIds.has(entity)) obstacleIds.set(entity, obstacleIds.size);
      }
    }
  }
  if (enabledPositions.length === 0) return [];

  // Require at least one useful contact whenever the board has an eligible obstacle.
  const configuredMinimum = Math.max(
    0,
    Math.floor(config.minimumObstacleContacts),
  );
  const requestedObstacleContacts = obstacleIds.size
    ? Math.max(1, Math.min(configuredMinimum, obstacleIds.size, pathLength))
    : 0;

  // Try each randomized board-crossing direction at the configured contact target.
  const directions: WhirlwindDancerPathDirection[] = [
    { axis: 'x', start: minX, end: maxX },
    { axis: 'x', start: maxX, end: minX },
    { axis: 'y', start: minY, end: maxY },
    { axis: 'y', start: maxY, end: minY },
  ];
  arrayShuffle(directions, random);
  for (const direction of directions) {
    const path = findWhirlwindDancerPath(
      map,
      enabledPositions,
      obstacleIds,
      pathLength,
      requestedObstacleContacts,
      random,
      direction,
    );
    if (path) return path;
  }

  // Keep the path useful on irregular boards where an edge-to-edge route is impossible.
  const usefulFallback = findWhirlwindDancerPath(
    map,
    enabledPositions,
    obstacleIds,
    pathLength,
    requestedObstacleContacts,
    random,
  );
  if (usefulFallback) return usefulFallback;

  // Use a valid random walk as the final safe fallback for disconnected boards.
  return createWhirlwindDancerFallbackPath(
    map,
    enabledPositions,
    obstacleIds,
    pathLength,
    random,
  );
}

/**
 * Execute Whirlwind Dancer against the settled Match-2 board.
 *
 * @param game Active Dream Drift Match-2 runtime.
 */
export async function runWhirlwindDancerPower(game: GameScene): Promise<void> {
  // Generate the complete logical path before the board begins changing.
  const map = game.sessionEntity.c.map;
  const path = generateWhirlwindDancerPath(map, WHIRLWIND_DANCER_POWER_CONFIG);

  // Snapshot every first-contact obstacle before animation callbacks mutate the board.
  const targetsByPathIndex = getWhirlwindDancerPathTargets(map, path);
  const appliedTargets = new Set<BlockEntity>();

  /** Apply every first-contact obstacle assigned to one reached path cell. */
  const applyPathStep = (pathIndex: number): void => {
    // Ignore invalid or target-free path callbacks without changing the board.
    const targets = targetsByPathIndex.get(pathIndex);
    if (!targets) return;

    // Apply one normal hit to every obstacle first contacted at this path index.
    for (const target of targets) {
      if (appliedTargets.has(target)) continue;
      appliedTargets.add(target);
      target.c.block.collide('attack', { type: 'booster' });
    }
  };

  // Let the travelling whirlwind synchronize each logical path contact.
  await runWhirlwindDancerPowerAnimation(game, path, applyPathStep);

  // Target-free paths still play their presentation without board settlement work.
  const targetCount = appliedTargets.size;
  if (targetCount === 0) return;

  // Give the collision system time to register its asynchronous activations.
  await waitAFrame();

  // Keep puzzle input blocked until all resulting falls and reactions have settled.
  await game.sessionEntity.c.phase.waitActivationsComplete();

  // Wait for the normal input phase after every resulting board reaction settles.
  if (game.phaseSystem.phase !== 'input') {
    await game.phaseSystem.waitForPhase('input');
  }
}

/**
 * Find an exact-length path satisfying the requested edge and contact constraints.
 *
 * @param map Active Match-2 board map.
 * @param enabledPositions Every enabled position on the board.
 * @param obstacleIds Stable ids for eligible obstacle entities.
 * @param pathLength Exact number of positions required in the path.
 * @param requiredObstacleContacts Minimum unique obstacle contacts required.
 * @param random Random source used to vary equally valid paths.
 * @param direction Optional opposite-edge constraint.
 * @returns A matching path, or undefined when no matching path exists.
 */
function findWhirlwindDancerPath(
  map: MapComponent,
  enabledPositions: PositionType[],
  obstacleIds: Map<BlockEntity, number>,
  pathLength: number,
  requiredObstacleContacts: number,
  random: RandomFunction,
  direction?: WhirlwindDancerPathDirection,
): PositionType[] | undefined {
  // Start from the requested entry edge, or from any enabled tile for fallback search.
  const startPositions = direction
    ? enabledPositions.filter(
        (position) => position[direction.axis] === direction.start,
      )
    : [...enabledPositions];
  arrayShuffle(startPositions, random);

  // Seed a depth-first search from every valid randomized start tile.
  const stack: WhirlwindDancerPathState[] = [];
  const visited = new Set<string>();
  for (const position of startPositions) {
    const contactedObstacleIds = mergeWhirlwindDancerObstacleContacts(
      [],
      getWhirlwindDancerCellObstacleIds(map.getCellAt(position), obstacleIds),
      requiredObstacleContacts,
    );
    const state = {
      position,
      contactedObstacleIds,
      path: [{ ...position }],
    };
    stack.push(state);
    visited.add(getWhirlwindDancerPathStateKey(state));
  }

  // Explore turn-biased orthogonal walks until an exact-length route is found.
  while (stack.length) {
    const state = stack.pop()!;

    // Accept only complete paths that reach the opposite edge and contact target.
    if (state.path.length === pathLength) {
      const reachedEnd =
        !direction || state.position[direction.axis] === direction.end;
      if (reachedEnd && state.contactedObstacleIds === undefined) {
        return state.path;
      }
      continue;
    }

    // Build each valid continuation with its contact and bend priority.
    const remainingStepsAfterNext = pathLength - state.path.length - 1;
    const candidates = getWhirlwindDancerNeighbors(map, state.position)
      .filter((position) => {
        // Preserve enough remaining steps to reach the required opposite edge.
        if (!direction) return true;
        const distanceToEnd = Math.abs(
          position[direction.axis] - direction.end,
        );
        return distanceToEnd <= remainingStepsAfterNext;
      })
      .map((position) => {
        const cellObstacleIds = getWhirlwindDancerCellObstacleIds(
          map.getCellAt(position),
          obstacleIds,
        );
        const contactedObstacleIds = mergeWhirlwindDancerObstacleContacts(
          state.contactedObstacleIds,
          cellObstacleIds,
          requiredObstacleContacts,
        );
        const step = {
          x: position.x - state.position.x,
          y: position.y - state.position.y,
        };

        // Prefer new useful contacts before choosing the path's visual shape.
        const completedContacts =
          state.contactedObstacleIds !== undefined &&
          contactedObstacleIds === undefined;
        const addedContact =
          state.contactedObstacleIds !== undefined &&
          contactedObstacleIds !== undefined &&
          contactedObstacleIds.length > state.contactedObstacleIds.length;
        let contactPriority = 0;
        if (completedContacts) contactPriority = 2;
        else if (addedContact) contactPriority = 1;

        // Prefer perpendicular turns, then straight motion, then backtracking.
        let bendPriority = 1;
        if (state.step) {
          const dot = state.step.x * step.x + state.step.y * step.y;
          if (dot === 0) bendPriority = 2;
          else if (dot < 0) bendPriority = 0;
        }

        // Contact priority dominates while bend priority shapes equal routes.
        return {
          position,
          contactedObstacleIds,
          step,
          priority: contactPriority * 10 + bendPriority,
        };
      });
    arrayShuffle(candidates, random);
    candidates.sort((left, right) => left.priority - right.priority);

    // Push lower-priority routes first so the stack explores bendier paths next.
    for (const candidate of candidates) {
      const nextState = {
        position: candidate.position,
        contactedObstacleIds: candidate.contactedObstacleIds,
        path: [...state.path, { ...candidate.position }],
        step: candidate.step,
      };
      const stateKey = getWhirlwindDancerPathStateKey(nextState);
      if (visited.has(stateKey)) continue;

      // Explore each distinct position/contact/length/direction state once.
      visited.add(stateKey);
      stack.push(nextState);
    }
  }

  // No exact-length route satisfies these constraints.
  return undefined;
}

/**
 * Create a valid best-effort random walk when the constrained search has no route.
 *
 * @param map Active Match-2 board map.
 * @param enabledPositions Every enabled position on the board.
 * @param obstacleIds Stable ids for eligible obstacle entities.
 * @param pathLength Exact number of positions requested.
 * @param random Random source used to vary the fallback walk.
 * @returns Best-effort path that remains useful when any obstacle is available.
 */
function createWhirlwindDancerFallbackPath(
  map: MapComponent,
  enabledPositions: PositionType[],
  obstacleIds: Map<BlockEntity, number>,
  pathLength: number,
  random: RandomFunction,
): PositionType[] {
  // Seed on an obstacle tile when possible so the fallback cannot completely whiff.
  const usefulStarts = enabledPositions.filter(
    (position) =>
      getWhirlwindDancerCellObstacleIds(map.getCellAt(position), obstacleIds)
        .length > 0,
  );
  const startPool = usefulStarts.length ? usefulStarts : enabledPositions;
  const start = startPool[Math.floor(random() * startPool.length)];
  const path = [{ ...start }];

  // Continue through random enabled neighbors until the configured length is met.
  while (path.length < pathLength) {
    const neighbors = getWhirlwindDancerNeighbors(map, path[path.length - 1]);
    const next = neighbors[Math.floor(random() * neighbors.length)];

    // Repeat an isolated tile only when no actual step is possible.
    path.push({ ...(next ?? path[path.length - 1]) });
  }

  // Return the complete best-effort walk.
  return path;
}

/**
 * Read eligible obstacle ids present at one board cell.
 *
 * @param cell Board cell being inspected.
 * @param obstacleIds Stable ids for eligible obstacle entities.
 * @returns Unique obstacle ids present in the cell.
 */
function getWhirlwindDancerCellObstacleIds(
  cell: Cell | undefined,
  obstacleIds: Map<BlockEntity, number>,
): number[] {
  // Missing or disabled cells cannot contribute obstacle contacts.
  if (!cell?.enabled) return [];

  // Read the exposed overlay obstacle first when the cell has one.
  const ids: number[] = [];
  const overlayEntity = cell.overlay?.entity;
  const overlayId = overlayEntity ? obstacleIds.get(overlayEntity) : undefined;
  if (overlayId !== undefined) ids.push(overlayId);

  // Count the base obstacle only when the overlay does not shield it from hits.
  const baseEntity = cell.base?.entity;
  const baseIsBlocked =
    overlayEntity &&
    baseEntity !== overlayEntity &&
    (overlayEntity.c.block.props.freeze || overlayEntity.c.block.props.bind);
  const baseId = baseEntity ? obstacleIds.get(baseEntity) : undefined;
  if (!baseIsBlocked && baseId !== undefined && !ids.includes(baseId)) {
    ids.push(baseId);
  }

  // Return every unique obstacle contacted by this tile.
  return ids;
}

/**
 * Merge newly contacted obstacle ids into one path search state.
 *
 * @param contactedObstacleIds Obstacle ids already contacted, or undefined once complete.
 * @param cellObstacleIds Obstacle ids present at the new cell.
 * @param requiredObstacleContacts Minimum unique contacts required.
 * @returns Updated ids, or undefined once the contact target is met.
 */
function mergeWhirlwindDancerObstacleContacts(
  contactedObstacleIds: number[] | undefined,
  cellObstacleIds: number[],
  requiredObstacleContacts: number,
): number[] | undefined {
  // Preserve the completed marker and handle paths with no contact requirement.
  if (contactedObstacleIds === undefined || requiredObstacleContacts === 0) {
    return undefined;
  }

  // Add each newly contacted unique obstacle id.
  const merged = [...contactedObstacleIds];
  for (const obstacleId of cellObstacleIds) {
    if (!merged.includes(obstacleId)) merged.push(obstacleId);
  }

  // Collapse completed contact sets so later search states can be deduplicated.
  if (merged.length >= requiredObstacleContacts) return undefined;

  // Keep incomplete ids ordered for a stable search-state key.
  return merged.sort((left, right) => left - right);
}

/**
 * Collect occupied, enabled orthogonal neighbors for one path position.
 *
 * @param map Active Match-2 board map.
 * @param position Current path endpoint.
 * @returns Enabled positions with base blocks reachable in one tile step.
 */
function getWhirlwindDancerNeighbors(
  map: MapComponent,
  position: PositionType,
): PositionType[] {
  // Test each orthogonal step against runtime board availability.
  const neighbors: PositionType[] = [];
  for (const offset of WHIRLWIND_DANCER_STEP_OFFSETS) {
    const next = { x: position.x + offset.x, y: position.y + offset.y };
    const cell = map.getCellAt(next);
    if (cell?.enabled && cell.base) neighbors.push(next);
  }

  // Return every valid one-step continuation.
  return neighbors;
}

/**
 * Build a stable key for one path search state.
 *
 * @param state Partial path search state.
 * @returns Position/contact/length key used for deduplication.
 */
function getWhirlwindDancerPathStateKey(
  state: WhirlwindDancerPathState,
): string {
  // Mark completed contact states independently of their previous obstacle ids.
  const contacts =
    state.contactedObstacleIds === undefined
      ? WHIRLWIND_DANCER_CONTACT_TARGET_MET
      : state.contactedObstacleIds.join(',');
  const step = state.step ? `${state.step.x},${state.step.y}` : 'start';

  // Include length and incoming direction because both affect remaining bends.
  return `${state.position.x}:${state.position.y}:${contacts}:${state.path.length}:${step}`;
}

/**
 * Collect unique eligible obstacle entities contacted by a path.
 *
 * @param map Active Match-2 board map.
 * @param path Ordered path positions.
 * @returns Unique obstacle entities in first-contact order.
 */
function getWhirlwindDancerPathTargets(
  map: MapComponent,
  path: PositionType[],
): WhirlwindDancerPathTargets {
  // Visit both block layers at every crossed tile.
  const targetsByPathIndex: WhirlwindDancerPathTargets = new Map();
  const seen = new Set<BlockEntity>();
  for (let pathIndex = 0; pathIndex < path.length; pathIndex++) {
    const position = path[pathIndex];
    const cell = map.getCellAt(position);
    const overlayEntity = cell?.overlay?.entity;
    const baseEntity = cell?.base?.entity;
    const baseIsBlocked =
      overlayEntity &&
      baseEntity !== overlayEntity &&
      (overlayEntity.c.block.props.freeze || overlayEntity.c.block.props.bind);
    const exposedEntities = [
      overlayEntity,
      baseIsBlocked ? undefined : baseEntity,
    ];
    for (const entity of exposedEntities) {
      if (!entity || seen.has(entity)) continue;
      if (!isWhirlwindDancerObstacle(entity)) continue;

      // Keep multi-cell and revisited obstacles to their first path contact.
      seen.add(entity);
      const existingTargets = targetsByPathIndex.get(pathIndex);
      if (existingTargets) {
        existingTargets.push(entity);
        continue;
      }

      // Start the first-contact target list for this path position.
      targetsByPathIndex.set(pathIndex, [entity]);
    }
  }

  // Return every unique eligible obstacle keyed by its first path contact.
  return targetsByPathIndex;
}

/**
 * Check whether a block entity is a destructible obstacle for this power.
 *
 * @param entity Match-2 block entity being inspected.
 * @returns Whether the entity can receive one Whirlwind Dancer hit.
 */
function isWhirlwindDancerObstacle(entity: BlockEntity): boolean {
  // Basic tiles, boosters, and immortal generators are not eligible obstacles.
  const block = entity.c.block;
  return (
    !WHIRLWIND_DANCER_INELIGIBLE_BLOCK_TYPES[block.props.type] &&
    !block.props.immortal
  );
}
